/* Re-Tech sell v2 — built from offer.js + app.js */
// Pure offer engine for sell v2. Shared by the theme asset and the Node tests.
const DEFAULT_CFG = {
  profitFloor: 40,        // € minimum net profit per card
  profitPct: 0.12,        // net profit as share of our resale price S
  refurb: 25,             // € cleaning, pads, paste, test
  handling: 10,           // € shipping / handling
  warrantyPct: 0.04,      // reserve for the 6-month warranty, share of S
  vatFactor: 1,           // 1 = not VAT-registered; 1.2 = margin scheme
  marketCap: 0.9,         // never pay more than this × M
  catalogueRatio: 0.82,   // models we don't sell: offer = ratio × M
  newToUsed: 1,           // Price Watch now writes used prices into the catalogue; keep 1 unless an entry is new-retail again
  maxAuto: 1500,          // € above this offer → manual quote (volatile, capital-heavy cards)
  minResale: 200,         // no auto-offer when S below this
  rangeLow: 0.95,         // shown range = [rangeLow × offer, offer]
  staleHours: 72,         // M older than this → not used as cap for shop models, catalogue models → manual
  cond: { a: 1, b: 0.9, c: 0.75 },
  tradeBonus: 0.10,       // store credit on top when traded in for a shop item
  buildBonus: 0.15        // store credit when put into a PC build
};

const r5 = v => Math.max(5, Math.round(v / 5) * 5);

/** model: {resale (S, € or null), market (M, € or null), marketAgeH (hours or null), locked}
 *  returns {kind:'offer', a, low, high, basis} or {kind:'manual', reason} */
function offerFor(model, condition, cfg = DEFAULT_CFG, opts = {}) {
  if (!model) return { kind: 'manual', reason: 'unknown' };
  if (opts.fault || condition === 'd') return { kind: 'manual', reason: 'fault' };
  const mul = cfg.cond[condition];
  if (!mul) return { kind: 'manual', reason: 'condition' };
  const S = Number(model.resale) || 0;
  const M = Number(model.market) || 0;
  const fresh = M > 0 && (model.locked || (model.marketAgeH != null && model.marketAgeH <= cfg.staleHours));
  let a, basis;
  if (S > 0) {
    if (S < cfg.minResale) return { kind: 'manual', reason: 'low_value' };
    const N = Math.max(cfg.profitFloor, cfg.profitPct * S);
    const formula = S - cfg.vatFactor * (N + cfg.refurb + cfg.warrantyPct * S + cfg.handling);
    // Shop M without a date is trusted (bot keeps it current); dated stale M is ignored.
    const useCap = M > 0 && (model.marketAgeH == null || fresh);
    a = useCap ? Math.min(formula, cfg.marketCap * M) : formula;
    basis = useCap && cfg.marketCap * M < formula ? 'market' : 'resale';
  } else if (M > 0 && fresh) {
    a = cfg.catalogueRatio * M;
    basis = 'catalogue';
  } else {
    return { kind: 'manual', reason: 'no_price' };
  }
  if (!(a > 0)) return { kind: 'manual', reason: 'no_price' };
  if (cfg.maxAuto && a > cfg.maxAuto) return { kind: 'manual', reason: 'high_value' };
  const high = r5(a * mul);
  const low = r5(a * mul * cfg.rangeLow);
  return { kind: 'offer', a: r5(a), low, high, basis };
}

const tradeCredit = (high, cfg = DEFAULT_CFG, build = false) =>
  r5(high * (1 + (build ? cfg.buildBonus : cfg.tradeBonus)));

/** Model key: rtx3060ti, rtx4070s, rx7900xtx … */
function modelKey(text) {
  const t = String(text || '').toLowerCase().replace(/[^a-z0-9а-я]+/g, ' ');
  let m = t.match(/\b(rtx|gtx)\s*(\d{4})\s*(ti)?\s*(super|s)?\b/);
  if (m) return m[1] + m[2] + (m[3] ? 'ti' : '') + (m[4] ? 's' : '');
  m = t.match(/\brx\s*(\d{4})\s*(xtx|xt|gre)?\b/);
  if (m) return 'rx' + m[1] + (m[2] || '');
  return '';
}

function vramOf(text) {
  const m = String(text || '').match(/\b(\d{1,2})\s*(?:gb|гб)\b/i);
  return m ? Number(m[1]) : null;
}

/* ---------- Re-Tech sell / trade-in v2 ---------- */
const root = document.querySelector('[data-rs2]');
if (root && !root.dataset.ready) init(root);

function init(root) {
  root.dataset.ready = '1';
  const raw = JSON.parse(root.querySelector('[data-rs2-data]').textContent);
  const L = root.dataset.locale === 'en' ? 'en' : 'bg';
  const C = raw.copy[L];
  const CFG = Object.assign({}, DEFAULT_CFG, raw.cfg, {cond: Object.assign({}, DEFAULT_CFG.cond, raw.cfg?.cond)});
  const app = root.querySelector('[data-rs2-app]');
  const form = root.querySelector('#rs2-form');
  const live = root.dataset.mode === 'live';
  const VIBER = root.dataset.viber || '+359885232362';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const fmt = v => Math.round(v).toLocaleString(L === 'en' ? 'en-IE' : 'bg-BG').replace(/ | /g, ' ') + ' €';
  const tpl = (s, o) => String(s).replace(/\{(\w+)\}/g, (_, k) => o[k] ?? '');

  /* ---------- tracking: GA4 (consent + live only) and dataLayer ---------- */
  const GA4 = 'G-QR3RPVJ3M1';
  const gaQueue = []; let gaReady = false;
  const analyticsOk = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const marketingOk = () => window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
  function gaInit() {
    if (gaReady) return true;
    if (!live || !analyticsOk()) return false;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const sc = document.createElement('script'); sc.async = true; sc.src = 'https://www.googletagmanager.com/gtag/js?id=' + GA4; document.head.append(sc);
      window.gtag('js', new Date());
    }
    window.gtag('config', GA4, {send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false});
    gaReady = true;
    gaQueue.splice(0).forEach(([n, p]) => window.gtag('event', n, p));
    return true;
  }
  try {
    document.addEventListener('visitorConsentCollected', gaInit);
    if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], err => { if (!err) gaInit(); });
  } catch {}
  const tracked = new Set();
  function track(name, params = {}, once = false) {
    if (once) { if (tracked.has(name + (params.rs2_label || ''))) return; tracked.add(name + (params.rs2_label || '')); }
    try { (window.dataLayer = window.dataLayer || []).push({event: 'rs2_sell', rs2_action: name, ...params}); } catch {}
    root.dataset.lastEvent = name;
    if (!live) return;
    const p = {...params, send_to: GA4, rs2_locale: L};
    if (gaInit()) window.gtag('event', name, p); else if (gaQueue.length < 40) gaQueue.push([name, p]);
  }

  /* ---------- models ---------- */
  const now = (raw.now || Math.floor(Date.now() / 1000)) * 1000;
  const short = t => String(t).replace(/^(nvidia\s+)?geforce\s+/i, '').replace(/^nvidia\s+/i, '').replace(/^amd\s+radeon\s+/i, '').replace(/^amd\s+/i, '').trim();
  const vramsOf = g => [...new Set((g.vars || []).map(v => vramOf(v[1])).filter(Boolean))];
  const priceFor = (g, vram) => {
    const vs = (g.vars || []).filter(v => !vram || !vramOf(v[1]) || vramOf(v[1]) === vram).map(v => v[0]);
    return vs.length ? Math.min(...vs) : g.S;
  };
  const shop = (raw.gpus || []).map(g => ({...g, key: modelKey(g.t)})).filter(g => g.key);
  const models = [];
  for (const g of shop.filter(g => !g.exact)) {
    const vr = vramsOf(g);
    const list = vr.length > 1 ? vr.sort((a, b) => b - a) : [vr[0] || g.vram || vramOf(g.t) || null];
    for (const v of list) {
      const base = short(g.t).replace(/\s*\d{1,2}\s*GB\b/i, '').trim();
      models.push({id: g.h + (vr.length > 1 ? '-' + v : ''), key: g.key, vram: v, label: base + (v ? ' ' + v + 'GB' : ''),
        resale: priceFor(g, v), market: g.M || null, marketAgeH: null, perf: g.perf, product: g, vendor: /^rx/.test(g.key) ? 'AMD' : 'NVIDIA'});
    }
  }
  for (const c of (raw.catalogue?.models || [])) {
    const key = modelKey(c.title); if (!key || !c.vramGb) continue;
    if (models.some(m => m.key === key && (!m.vram || m.vram === c.vramGb))) continue;
    const age = c.observedAt ? (now - Date.parse(c.observedAt)) / 3.6e6 : null;
    models.push({id: c.id || key + '-' + c.vramGb, key, vram: c.vramGb, label: short(c.title).replace(/\s*\d{1,2}\s*GB\b/i, '').trim() + ' ' + c.vramGb + 'GB',
      resale: null, market: Number(c.marketPriceEur) ? Number(c.marketPriceEur) * (c.valuationBasis?.condition === 'new' ? CFG.newToUsed : 1) : null, marketAgeH: age == null || age < 0 ? 1e9 : age, locked: c.manualLock === true, perf: null, product: null, vendor: /^rx/.test(key) ? 'AMD' : 'NVIDIA'});
  }
  models.sort((a, b) => a.key === b.key ? (b.vram || 0) - (a.vram || 0) : a.label.localeCompare(b.label, 'en', {numeric: true}));
  const norm = s => String(s || '').toLowerCase().replace(/geforce|radeon|nvidia|amd/g, '').replace(/[^a-z0-9]+/g, '');

  /* ---------- state ---------- */
  const st = {cat: 'gpu', q: '', model: null, cond: null, fault: false, mined: false, res: '1440p', target: null, view: 'offer', open: false, hi: -1};
  const offer = () => st.cat !== 'gpu' || !st.cond ? null : st.model ? offerFor(st.model, st.cond, CFG, {fault: st.fault}) : st.q.trim().length >= 3 ? {kind: 'manual', reason: st.fault ? 'fault' : 'unknown'} : null;
  const credit = (o, build) => o?.kind === 'offer' ? tradeCredit(o.high, CFG, build) : 0;

  /* ---------- performance / upgrades ---------- */
  const GAMES = {'cyberpunk-2077': 'Cyberpunk 2077', 'black-myth-wukong': 'Black Myth: Wukong', 'warzone': 'Warzone', 'cod-black-ops-6': 'Black Ops 6', 'rdr2': 'RDR2', 'fortnite': 'Fortnite', 'cs2': 'CS2'};
  function gain(src, tgt, res) {
    if (!src?.g || !tgt?.g) return null;
    let sum = 0, n = 0, best = null;
    for (const g of Object.keys(GAMES)) {
      const a = src.g[g]?.[res]?.[0], b = tgt.g[g]?.[res]?.[0];
      if (a > 0 && b > 0) { sum += b / a - 1; n++; }
    }
    return n >= 3 ? {pct: Math.round(sum / n * 100), n} : null;
  }
  function upgrades(o) {
    const src = st.model?.perf; if (!src || o?.kind !== 'offer') return [];
    const cr = credit(o, false);
    const cands = shop.filter(g => g.perf && (!g.exact || g.avail) && g.key !== st.model.key).map(g => {
      const S = priceFor(g, g.vram || null); const gn = gain(src, g.perf, st.res);
      return gn && gn.pct >= 20 && S > cr && S - cr <= Math.max(600, 2.5 * cr) ? {g, S, pay: S - cr, gn} : null;
    }).filter(Boolean).sort((a, b) => a.pay - b.pay);
    // Same brand first (NVIDIA owners usually want DLSS), then fill from the other brand.
    const vendorOf = g => /^rx/.test(g.key) ? 'AMD' : 'NVIDIA';
    const ladder = list => { const out = []; let last = 0; for (const c of list) { if (out.length >= 3) break; if (!out.length || c.gn.pct >= last + 15) { out.push(c); last = c.gn.pct; } } return out; };
    let out = ladder(cands.filter(c => vendorOf(c.g) === st.model.vendor));
    if (out.length < 3) out = out.concat(ladder(cands.filter(c => vendorOf(c.g) !== st.model.vendor && !out.includes(c))).slice(0, 3 - out.length));
    return out.sort((a, b) => a.pay - b.pay);
  }

  /* ---------- rendering ---------- */
  const icon = {
    gpu: '<svg viewBox="0 0 28 28" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 5.5v17h2M6 7.5h18a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H6V7.5ZM9 19.5v3h5M16 19.5v3h4"/><circle cx="11" cy="13.5" r="3.3"/><circle cx="20.5" cy="13.5" r="3.3"/></svg>',
    other: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="2"/><rect x="9.5" y="9.5" width="5" height="5" rx=".8"/><path d="M9 2.5V6M15 2.5V6M9 18v3.5M15 18v3.5M2.5 9H6M2.5 15H6M18 9h3.5M18 15h3.5"/></svg>',
    pc: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="2.5" width="12" height="19" rx="2"/><path d="M9 6.5h6M9 9.5h6"/><circle cx="12" cy="16" r="2.2"/></svg>',
    check: '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3 8.5 3 3 7-7"/></svg>',
    viber: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3c5 0 8 2.4 8 7.5S17 18 12 18l-4 3v-3.4C5.6 16.7 4 14.4 4 10.5 4 5.4 7 3 12 3Z"/><path d="M9.5 8.5c0 3 2 5 5 5l1-1-1.5-1-.8.6c-1-.4-1.9-1.3-2.3-2.3l.6-.8-1-1.5-1 1Z"/></svg>'
  };

  app.innerHTML = `
    <header class="rs2-head">
      <span class="rs2-kicker">${esc(C.eyebrow)}</span>
      <h1 class="rs2-h1">${esc(C.h1)}</h1>
      <p class="rs2-intro">${esc(C.intro)}</p>
      <ul class="rs2-trust">${C.trust.map(x => `<li>${icon.check}${esc(x)}</li>`).join('')}</ul>
    </header>
    <div class="rs2-grid">
      <div class="rs2-main" data-r="main">
        <fieldset class="rs2-block">
          <legend class="rs2-q">${esc(C.cat_label)}</legend>
          <div class="rs2-cats">${['gpu', 'other', 'pc'].map(c => `<label class="rs2-cat"><input type="radio" name="rs2cat" value="${c}" ${st.cat === c ? 'checked' : ''}><span>${icon[c]}<strong>${esc(C['cat_' + c])}</strong></span></label>`).join('')}</div>
        </fieldset>
        <div data-r="gpuin"></div>
        <div data-r="otherin" hidden>
          <label class="rs2-field"><span class="rs2-label">${esc(C.other_label)}</span>
            <textarea rows="3" maxlength="1000" data-k="describe" placeholder="${esc(C.other_ph)}"></textarea></label>
          <p class="rs2-note" data-r="othernote"></p>
        </div>
      </div>
      <aside class="rs2-side" data-r="side"></aside>
    </div>
    <section class="rs2-up" data-r="up" hidden></section>
    <section class="rs2-formwrap" data-r="formwrap" hidden></section>
    <section class="rs2-faq"><h2 class="rs2-h2">${esc(C.faq_title)}</h2>${C.faq.map(([q, a]) => `<details><summary>${esc(q)}</summary><p>${esc(a)}</p></details>`).join('')}</section>`;
  // Price bar lives on <body>: a transformed/overflow theme wrapper would otherwise break position:fixed.
  const dockHost = document.createElement('div');
  dockHost.className = 'rs2 rs2--dock';
  dockHost.innerHTML = '<div class="rs2-dock" hidden></div>';
  document.body.append(dockHost);
  const dockEl = dockHost.firstElementChild;

  const R = k => app.querySelector(`[data-r="${k}"]`);
  R('gpuin').innerHTML = `
    <div class="rs2-block rs2-search">
      <label class="rs2-field"><span class="rs2-q">${esc(C.model_label)}</span>
        <input type="text" autocomplete="off" spellcheck="false" role="combobox" aria-autocomplete="list" aria-expanded="false" aria-controls="rs2-list" data-rs2-search placeholder="${esc(C.model_ph)}" maxlength="80"></label>
      <ul class="rs2-list" id="rs2-list" role="listbox" hidden></ul>
      <p class="rs2-help" data-r="mhelp">${esc(C.model_help)}</p>
    </div>
    <fieldset class="rs2-block">
      <legend class="rs2-q">${esc(C.cond_label)}</legend>
      <div class="rs2-conds">${['a', 'b', 'c', 'd'].map(c => `<label class="rs2-cond"><input type="radio" name="rs2cond" value="${c}"><span><strong>${esc(C['cond_' + c])}</strong><small>${esc(C['cond_' + c + '_d'])}</small></span></label>`).join('')}</div>
      <label class="rs2-check"><input type="checkbox" data-k="mined"><span>${esc(C.mined)}</span></label>
    </fieldset>`;
  // Move the native form into the page flow.
  R('formwrap').append(form);
  const search = app.querySelector('[data-rs2-search]');
  const list = app.querySelector('#rs2-list');

  function suggestions() {
    const q = norm(st.q);
    if (!q) return models.slice(0, 0);
    return models.filter(m => norm(m.label).includes(q) || m.key.includes(q)).slice(0, 8);
  }
  function renderList() {
    const items = suggestions();
    const show = st.open && st.q && !(st.model && st.model.label === st.q);
    list.hidden = !show; search.setAttribute('aria-expanded', String(show));
    if (!show) return;
    list.innerHTML = items.length ? items.map((m, i) => `<li role="option" id="rs2-o${i}" data-id="${esc(m.id)}" aria-selected="${i === st.hi}" class="${i === st.hi ? 'is-hi' : ''}"><span>${esc(m.label)}</span><small>${esc(m.vendor)}</small></li>`).join('') : `<li class="rs2-empty" aria-disabled="true">${esc(C.model_none)}</li>`;
    search.setAttribute('aria-activedescendant', st.hi >= 0 ? 'rs2-o' + st.hi : '');
  }
  function pickModel(m) {
    st.model = m; st.q = m.label; search.value = m.label; st.open = false; st.target = null;
    track('sell_model', {rs2_label: m.label});
    render();
  }

  function offerCard(o) {
    if (st.cat !== 'gpu') {
      return `<div class="rs2-card rs2-card--manual"><span class="rs2-kicker">${esc(C.manual_title)}</span><p>${esc(st.cat === 'pc' ? C.manual_pc : C.manual_other)}</p>${ctaBtns(false)}</div>`;
    }
    if (!o) {
      return `<div class="rs2-card rs2-card--empty"><span class="rs2-kicker">${esc(C.offer_label)}</span><p class="rs2-big rs2-big--dim">— €</p><p class="rs2-small">${esc(C.model_help)}</p></div>`;
    }
    if (o.kind !== 'offer') {
      return `<div class="rs2-card rs2-card--manual"><span class="rs2-kicker">${esc(C.manual_title)}</span><p>${esc(C['manual_' + (o.reason === 'fault' ? 'fault' : o.reason === 'low_value' ? 'low_value' : o.reason === 'unknown' ? 'unknown' : o.reason === 'high_value' ? 'high_value' : 'no_price')])}</p>${ctaBtns(false)}</div>`;
    }
    const tc = credit(o, false), bc = credit(o, true);
    return `<div class="rs2-card rs2-card--offer">
      <span class="rs2-kicker">${esc(C.offer_label)} · ${esc(st.model.label)}</span>
      <p class="rs2-big">${fmt(o.low).replace(' €', '')}–${fmt(o.high)}</p>
      <dl class="rs2-credits">
        <div><dt>${esc(C.offer_trade)} <em>+${Math.round(CFG.tradeBonus * 100)}%</em></dt><dd>${fmt(tc)}</dd></div>
        <div><dt>${esc(C.offer_build)} <em>+${Math.round(CFG.buildBonus * 100)}%</em></dt><dd>${fmt(bc)}</dd></div>
      </dl>
      <p class="rs2-small">${esc(C.offer_note)}</p>
      ${ctaBtns(true)}
    </div>`;
  }
  function ctaBtns(withDeposit) {
    const dep = withDeposit && st.target && raw.deposit;
    return `<div class="rs2-ctas">
      <button type="button" class="rs2-btn rs2-btn--primary" data-act="form">${esc(C.next)}</button>
      ${dep ? `<button type="button" class="rs2-btn" data-act="deposit">${esc(C.deposit_cta)}</button><p class="rs2-small">${esc(C.deposit_sub)}</p>` : ''}
      <a class="rs2-btn rs2-btn--ghost" href="${esc(viberLink())}" data-act="viber" rel="nofollow">${icon.viber}${esc(C.viber)}</a>
    </div>`;
  }
  function viberLink() {
    const o = offer();
    const what = st.cat === 'gpu' && st.model ? `${st.model.label}${st.cond ? ' (' + C['cond_' + st.cond] + ')' : ''}` : (app.querySelector('[data-k="describe"]')?.value.trim() || C['cat_' + st.cat]);
    const msg = (L === 'en' ? 'Hi, I want to sell: ' : 'Здравейте, искам да продам: ') + what + (o?.kind === 'offer' ? ` — ${L === 'en' ? 'website offer' : 'оферта от сайта'} ${o.low}–${o.high} €` : '') + (st.target ? ` → ${st.target.title}` : '') + '\n' + location.origin + location.pathname;
    return `viber://chat?number=${encodeURIComponent(VIBER)}&draft=${encodeURIComponent(msg)}`;
  }
  function upCards(o) {
    if (st.cat !== 'gpu' || o?.kind !== 'offer') return '';
    const ups = upgrades(o), bc = credit(o, true);
    const resBtns = ['1080p', '1440p', '4K'].map(r => `<button type="button" class="rs2-chip ${st.res === r ? 'is-on' : ''}" data-act="res" data-v="${r}" aria-pressed="${st.res === r}">${r}</button>`).join('');
    const cards = ups.map(u => {
      const sel = st.target?.h === u.g.h;
      return `<article class="rs2-upc ${sel ? 'is-sel' : ''}">
        ${u.g.img ? `<img src="${esc(u.g.img)}" alt="" width="120" height="120" loading="lazy">` : ''}
        <div><h3>${esc(short(u.g.t))}</h3>
        <p class="rs2-fps">${esc(tpl(C.up_fps, {pct: u.gn.pct, n: u.gn.n, res: st.res}))}</p>
        <p class="rs2-pay"><small>${esc(C.up_pay)}</small> <strong>${fmt(u.pay)}</strong> <s>${fmt(u.S)}</s></p>
        <button type="button" class="rs2-btn ${sel ? 'rs2-btn--primary' : ''}" data-act="target" data-v="${esc(u.g.h)}" data-s="${u.S}" aria-pressed="${sel}">${esc(sel ? C.up_chosen : C.up_choose)}</button></div>
      </article>`;
    }).join('');
    return `${ups.length ? `<div class="rs2-uphead"><div><h2 class="rs2-h2">${esc(C.up_title)}</h2><p class="rs2-small">${esc(tpl(C.up_sub, {bonus: Math.round(CFG.tradeBonus * 100)}))}</p></div><div class="rs2-chips" role="group" aria-label="${esc(C.res_label)}">${resBtns}</div></div><div class="rs2-ups">${cards}</div>` : ''}
      <article class="rs2-build"><div><h2 class="rs2-h2">${esc(C.build_title)}</h2><p>${esc(tpl(C.build_sub, {credit: bc, bonus: Math.round(CFG.buildBonus * 100)}))}</p></div>
      <a class="rs2-btn rs2-btn--primary" data-act="build" href="${esc(buildLink(o))}">${esc(C.build_cta)}</a></article>`;
  }
  function buildLink(o) {
    const txt = `${st.model.label}, ${C['cond_' + st.cond]} — ${L === 'en' ? 'website offer' : 'оферта от сайта'} ${o.low}–${o.high} €, ${L === 'en' ? 'credit towards a PC' : 'кредит за компютър'} ${credit(o, true)} €`;
    return raw.configurator + '#trade=' + encodeURIComponent(txt);
  }
  function dock(o) {
    const d = dockEl;
    const show = st.view === 'offer' && ((st.cat === 'gpu' && o?.kind === 'offer') || (st.cat !== 'gpu'));
    d.hidden = !show;
    if (!show) return;
    d.innerHTML = `<div><small>${esc(st.cat === 'gpu' ? C.offer_label : C.manual_title)}</small><strong>${o?.kind === 'offer' ? fmt(o.low).replace(' €', '') + '–' + fmt(o.high) : '—'}</strong></div><button type="button" class="rs2-btn rs2-btn--primary" data-act="form">${esc(C.next)}</button>`;
  }

  function render() {
    const o = offer();
    R('gpuin').hidden = st.cat !== 'gpu';
    R('otherin').hidden = st.cat === 'gpu';
    R('othernote').textContent = st.cat === 'pc' ? C.manual_pc : C.manual_other;
    R('side').innerHTML = offerCard(o);
    const up = upCards(o); R('up').hidden = !up; R('up').innerHTML = up;
    app.querySelectorAll('input[name="rs2cond"]').forEach(i => { i.checked = i.value === st.cond; });
    dock(o);
    renderList();
    if (o?.kind === 'offer') track('sell_offer', {rs2_label: st.model.label + '/' + st.cond, value: o.high, currency: 'EUR'}, true);
  }

  // The theme cancels native smooth scrolling, so animate with instant steps instead.
  function glideTo(y) {
    y = Math.max(0, Math.round(y));
    if (reduced) { scrollTo(0, y); return; }
    const from = scrollY, d = y - from, t0 = performance.now(), dur = 380;
    if (Math.abs(d) < 4) return;
    let done = false;
    const step = now => { if (done) return; const k = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - k, 3); scrollTo(0, from + d * e); if (k < 1) requestAnimationFrame(step); else done = true; };
    requestAnimationFrame(step);
    // Safety net: if frames are throttled or something cancels the glide, land exactly on target.
    setTimeout(() => { if (!done || Math.abs(scrollY - y) > 4) { done = true; scrollTo(0, y); } }, dur + 120);
  }
  const glideToEl = (el, off = 80) => glideTo(scrollY + el.getBoundingClientRect().top - off);
  /* ---------- form step ---------- */
  const f = (k, label, type = 'text', extra = '') => `<label class="rs2-field"><span class="rs2-label">${esc(label)}</span>${type === 'textarea' ? `<textarea rows="3" maxlength="1000" data-f="${k}" ${extra}></textarea>` : `<input type="${type}" data-f="${k}" ${extra}>`}<span class="rs2-err" data-err="${k}" hidden></span></label>`;
  form.querySelector('[data-rs2-formslot]').innerHTML = `
    <div class="rs2-summary" data-r2="summary"></div>
    <div class="rs2-formcols">
      <fieldset class="rs2-block" data-r2="details"><legend class="rs2-q">${esc(C.details_title)}</legend><p class="rs2-small">${esc(C.details_sub)}</p>
        ${f('edition', C.edition_label, 'text', `maxlength="120" placeholder="${esc(C.edition_ph)}"`)}
        ${f('warranty', C.warranty_label, 'text', 'maxlength="120"')}
        <label class="rs2-check"><input type="checkbox" data-f="box"><span>${esc(C.box_label)}</span></label>
        ${f('photos', C.photos_label, 'url', 'maxlength="300" placeholder="https://"')}
        ${f('notes', C.notes_label, 'textarea')}
      </fieldset>
      <fieldset class="rs2-block"><legend class="rs2-q">${esc(C.contact_title)}</legend>
        ${f('name', C.name, 'text', 'name="contact[name]" autocomplete="name" maxlength="80" required')}
        ${f('email', C.email, 'email', 'name="contact[email]" autocomplete="email" maxlength="120" required')}
        ${f('phone', C.phone, 'tel', 'name="contact[Phone]" autocomplete="tel" maxlength="30" required')}
        ${f('city', C.city, 'text', 'name="contact[Location]" autocomplete="address-level2" maxlength="80"')}
        ${f('address', C.address || (L === 'en' ? 'Address' : 'Адрес'), 'text', `name="contact[Адрес]" autocomplete="street-address" maxlength="160" required placeholder="${esc(L === 'en' ? 'Town, street, number' : 'Град, улица, номер')}"`)}
        ${f('price', C.price_label, 'number', 'inputmode="numeric" min="0" max="20000"')}
        <label class="rs2-check"><input type="checkbox" data-f="consent"><span>${esc(C.consent)} <a href="${esc(raw.privacy)}" target="_blank" rel="noopener">${esc(C.privacy)}</a></span></label>
        <span class="rs2-err" data-err="consent" hidden></span>
        <div class="rs2-ctas"><button type="submit" class="rs2-btn rs2-btn--primary" data-r2="submit">${esc(C.submit)}</button>
        <button type="button" class="rs2-btn rs2-btn--ghost" data-act="back">${esc(C.back)}</button></div>
        <p class="rs2-note rs2-note--error" data-r2="ferr" role="alert" hidden></p>
      </fieldset>
    </div>`;
  form.noValidate = true;
  const F = k => form.querySelector(`[data-f="${k}"]`);
  const fv = k => (F(k)?.value || '').trim();

  function summaryHtml() {
    const o = offer();
    const what = st.cat === 'gpu' ? `${st.model?.label || st.q} · ${C['cond_' + st.cond] || ''}` : (app.querySelector('[data-k="describe"]').value.trim() || C['cat_' + st.cat]);
    return `<div><span class="rs2-kicker">${esc(o?.kind === 'offer' ? C.offer_label : C.manual_title)}</span><strong>${esc(what)}</strong>${o?.kind === 'offer' ? `<span class="rs2-sumprice">${fmt(o.low).replace(' €', '')}–${fmt(o.high)}</span>` : ''}${st.target ? `<span class="rs2-small">→ ${esc(st.target.title)}</span>` : ''}</div><button type="button" class="rs2-btn rs2-btn--ghost" data-act="back">${esc(C.edit)}</button>`;
  }
  function openForm() {
    if (st.cat === 'gpu' && (!st.model || !st.cond)) {
      // Unknown model typed: let them continue as manual.
      if (!st.q || !st.cond) { (st.q ? app.querySelector('input[name="rs2cond"]') : search).focus(); return; }
    }
    if (st.cat !== 'gpu' && !app.querySelector('[data-k="describe"]').value.trim()) { app.querySelector('[data-k="describe"]').focus(); return; }
    st.view = 'form';
    form.querySelector('[data-r2="summary"]').innerHTML = summaryHtml();
    form.querySelector('[data-r2="details"]').hidden = st.cat !== 'gpu';
    app.querySelector('.rs2-grid').hidden = true; R('up').hidden = true; R('formwrap').hidden = false;
    dock(offer());
    glideToEl(R('formwrap'));
    setTimeout(() => F('name').focus({preventScroll: true}), reduced ? 0 : 350);
    track('sell_form_open', {rs2_label: st.cat}, true);
  }
  function closeForm() {
    st.view = 'offer'; R('formwrap').hidden = true; app.querySelector('.rs2-grid').hidden = false; render();
    glideToEl(app.querySelector('.rs2-grid'));
  }

  function errFor(k) {
    const v = fv(k);
    if (k === 'name') return v.length >= 2 ? '' : C.err_required;
    if (k === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) ? '' : (v ? C.err_email : C.err_required);
    if (k === 'phone') return !v ? C.err_required : (window.ReTechPhone && !window.ReTechPhone.isValid(v) ? C.err_phone : '');
    if (k === 'address') return v.length >= 5 ? '' : C.err_required;
    if (k === 'photos') { if (!v) return ''; try { const u = new URL(v); return /^https?:$/.test(u.protocol) && !u.username && !u.password ? '' : C.err_url; } catch { return C.err_url; } }
    if (k === 'consent') return F('consent').checked ? '' : C.err_consent;
    return '';
  }
  function validate() {
    let first = null;
    for (const k of ['name', 'email', 'phone', 'address', 'photos', 'consent']) {
      const e = errFor(k), n = form.querySelector(`[data-err="${k}"]`);
      n.hidden = !e; n.textContent = e;
      F(k)?.setAttribute('aria-invalid', String(!!e));
      if (e && !first) first = k;
    }
    if (first) F(first).focus();
    return !first;
  }
  function bodyText() {
    const o = offer();
    const lines = [
      L === 'en' ? 'Sell / trade-in request (website, v2)' : 'Заявка за изкупуване от сайта (v2)', '',
      `${L === 'en' ? 'Type' : 'Тип'}: ${C['cat_' + st.cat]}`,
      st.cat === 'gpu' ? `${L === 'en' ? 'Model' : 'Модел'}: ${st.model?.label || st.q}` : `${L === 'en' ? 'Description' : 'Описание'}: ${app.querySelector('[data-k="describe"]').value.trim()}`,
      st.cat === 'gpu' ? `${L === 'en' ? 'Condition' : 'Състояние'}: ${C['cond_' + st.cond] || '-'}${st.mined ? ' · ' + C.mined : ''}` : '',
      st.cat === 'gpu' ? `${L === 'en' ? 'Brand / series' : 'Производител / серия'}: ${fv('edition') || '-'}` : '',
      st.cat === 'gpu' ? `${L === 'en' ? 'Warranty / purchase' : 'Гаранция / покупка'}: ${fv('warranty') || '-'}` : '',
      st.cat === 'gpu' ? `${L === 'en' ? 'Box / invoice' : 'Кутия / фактура'}: ${F('box').checked ? (L === 'en' ? 'yes' : 'да') : (L === 'en' ? 'no' : 'не')}` : '',
      `${L === 'en' ? 'Photos' : 'Снимки'}: ${fv('photos') || '-'}`,
      `${L === 'en' ? 'Asking price' : 'Желана цена'}: ${fv('price') ? fv('price') + ' €' : '-'}`,
      `${L === 'en' ? 'City' : 'Град'}: ${fv('city') || '-'}`, '',
      o?.kind === 'offer'
        ? `${L === 'en' ? 'Website offer' : 'Оферта от сайта'}: ${o.low}–${o.high} € (${L === 'en' ? 'basis' : 'база'}: ${o.basis}; S ${st.model.resale ?? '-'} €, M ${st.model.market ?? '-'} €)\n${L === 'en' ? 'Trade-in credit' : 'Кредит при замяна'}: ${credit(o)} € · ${L === 'en' ? 'towards a PC' : 'към компютър'}: ${credit(o, true)} €`
        : `${L === 'en' ? 'Website offer' : 'Оферта от сайта'}: ${L === 'en' ? 'manual' : 'ръчна оценка'}${o?.reason ? ' (' + o.reason + ')' : ''}`,
      st.target ? `${L === 'en' ? 'Wants instead' : 'Желан продукт (замяна)'}: ${st.target.title} — ${st.target.S} €, ${L === 'en' ? 'pays about' : 'доплаща около'} ${Math.max(0, st.target.S - credit(o))} €` : '',
      `${L === 'en' ? 'Notes' : 'Бележки'}: ${fv('notes') || '-'}`,
      `${L === 'en' ? 'Consent' : 'Съгласие за обработка'}: ${F('consent').checked ? (L === 'en' ? 'yes' : 'да') : ''}`
    ].filter(x => x !== '');
    return lines.join('\n');
  }

  let busy = false, timer = null;
  function setBusy(b) { busy = b; const btn = form.querySelector('[data-r2="submit"]'); btn.disabled = b; btn.textContent = b ? C.sending : C.submit; form.setAttribute('aria-busy', String(b)); }
  function fail(msg) { setBusy(false); const n = form.querySelector('[data-r2="ferr"]'); n.hidden = false; n.innerHTML = esc(msg) + ` <a href="${esc(viberLink())}">${esc(C.viber)}</a>`; }
  form.addEventListener('submit', e => {
    e.preventDefault();
    if (busy || !validate()) return;
    if (navigator.onLine === false) return fail(C.err_offline);
    form.querySelector('[data-rs2-body]').value = bodyText();
    const o = offer();
    try { sessionStorage.setItem('rs2.sent', JSON.stringify({t: Date.now(), path: location.pathname, cat: st.cat, model: st.model?.label || '', high: o?.kind === 'offer' ? o.high : 0, target: st.target?.title || '', email: fv('email')})); } catch {}
    setBusy(true);
    track('sell_submit', {rs2_label: st.cat, value: o?.kind === 'offer' ? o.high : 0, currency: 'EUR'});
    let started = false;
    const go = () => { if (started) return; started = true; clearTimeout(timer); timer = setTimeout(() => fail(C.err_unconfirmed), 30000); try { HTMLFormElement.prototype.submit.call(form); } catch { fail(C.err_unconfirmed); } };
    timer = setTimeout(() => { if (!started) fail(C.err_unconfirmed); }, 20000);
    try { if (window.Shopify?.captcha?.protect) window.Shopify.captcha.protect(form, go); else go(); } catch { go(); }
  });
  form.addEventListener('formdata', e => { ['rs2cat', 'rs2cond'].forEach(k => e.formData.delete(k)); });

  /* ---------- deposit (trade-in with a chosen target) ---------- */
  async function deposit(btn) {
    const o = offer(); if (!raw.deposit || !st.target || o?.kind !== 'offer') return;
    btn.disabled = true; btn.textContent = C.sending;
    const en = L === 'en';
    const props = {
      [en ? 'Type' : 'Тип']: en ? 'Trade-in reservation' : 'Резервация на замяна',
      [en ? 'Card to sell' : 'Карта за изкупуване']: `${st.model.label} · ${C['cond_' + st.cond]}${st.mined ? ' · ' + C.mined : ''}`,
      [en ? 'Website offer' : 'Оферта от сайта']: `${o.low}–${o.high} €`,
      [en ? 'Trade-in credit' : 'Кредит при замяна']: `${credit(o)} €`,
      [en ? 'Wanted product' : 'Желан продукт']: `${st.target.title} — ${st.target.S} €`,
      [en ? 'To pay (approx.)' : 'Доплащане (ориентировъчно)']: `${Math.max(0, st.target.S - credit(o))} €`,
      [en ? 'Price held until' : 'Цената важи до']: new Date(Date.now() + 7 * 864e5).toLocaleDateString(en ? 'en-GB' : 'bg-BG'),
      _rs2: 'v2'
    };
    const base = (window.Shopify?.routes?.root || '/');
    try {
      const cart = await (await fetch(base + 'cart.js', {headers: {Accept: 'application/json'}})).json();
      for (const it of cart.items || []) if (it.variant_id === raw.deposit.variant) await fetch(base + 'cart/change.js', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({id: it.key, quantity: 0})});
      const r = await fetch(base + 'cart/add.js', {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({items: [{id: raw.deposit.variant, quantity: 1, properties: props}]})});
      if (!r.ok) throw new Error('add');
      track('begin_checkout', {value: raw.deposit.price, currency: 'EUR', rs2_label: st.model.label, items: [{item_id: 'SRV-DEPOSIT', item_name: 'Deposit', price: raw.deposit.price, quantity: 1}]});
      location.href = base + 'checkout';
    } catch { btn.disabled = false; btn.textContent = C.deposit_cta; fail(C.err_unconfirmed); }
  }

  /* ---------- events ---------- */
  app.addEventListener('change', e => {
    const t = e.target;
    if (t.name === 'rs2cat') { st.cat = t.value; track('sell_cat', {rs2_label: st.cat}); render(); return; }
    if (t.name === 'rs2cond') { st.cond = t.value; st.fault = st.cond === 'd'; track('sell_cond', {rs2_label: st.cond}); render(); revealOffer(); return; }
    if (t.dataset.k === 'mined') { st.mined = t.checked; return; }
  });
  search.addEventListener('input', () => {
    st.q = search.value; st.open = true; st.hi = -1;
    if (st.model && st.model.label !== st.q) { st.model = null; st.target = null; }
    const items = suggestions();
    const exact = items.filter(m => norm(m.label) === norm(st.q));
    if (exact.length === 1) { pickModel(exact[0]); return; }
    render();
  });
  search.addEventListener('focus', () => { st.open = true; renderList(); });
  search.addEventListener('blur', () => setTimeout(() => { st.open = false; renderList(); }, 150));
  search.addEventListener('keydown', e => {
    const items = suggestions(); if (!items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); st.open = true; st.hi = Math.min(items.length - 1, st.hi + 1); renderList(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); st.hi = Math.max(0, st.hi - 1); renderList(); }
    else if (e.key === 'Enter') { e.preventDefault(); pickModel(items[Math.max(0, st.hi)]); }
    else if (e.key === 'Escape') { st.open = false; renderList(); }
  });
  list.addEventListener('mousedown', e => { const li = e.target.closest('li[data-id]'); if (!li) return; e.preventDefault(); const m = models.find(x => x.id === li.dataset.id); if (m) pickModel(m); });
  // Phones: after the condition is picked, bring the offer card into view (once per model).
  let revealed = '';
  function revealOffer() {
    if (matchMedia('(min-width: 960px)').matches || !offer()) return;
    const key = (st.model?.id || st.q) + st.cat; if (revealed === key) return; revealed = key;
    const card = R('side'); const top = card.getBoundingClientRect().top;
    if (top > innerHeight - 160 || top < 60) glideToEl(card);
  }
  const onClick = e => {
    const a = e.target.closest('[data-act]'); if (!a) return;
    const act = a.dataset.act;
    if (act === 'form') { e.preventDefault(); openForm(); }
    else if (act === 'back') { e.preventDefault(); closeForm(); }
    else if (act === 'res') { st.res = a.dataset.v; render(); }
    else if (act === 'target') {
      const g = shop.find(x => x.h === a.dataset.v);
      st.target = st.target?.h === g.h ? null : {h: g.h, title: short(g.t), S: Number(a.dataset.s)};
      track('sell_upgrade_pick', {rs2_label: st.target?.title || 'none'}); render();
    }
    else if (act === 'build') track('sell_build_click', {rs2_label: st.model?.label || ''});
    else if (act === 'viber') { a.href = viberLink(); track('sell_viber', {rs2_label: st.cat}); }
    else if (act === 'deposit') deposit(a);
  };
  root.addEventListener('click', onClick);
  dockHost.addEventListener('click', onClick);
  app.addEventListener('input', e => { if (e.target.dataset.k === 'describe') { /* keep viber link fresh */ } });

  /* ---------- after Shopify posts the form back ---------- */
  function success() {
    let sent = null; try { sent = JSON.parse(sessionStorage.getItem('rs2.sent') || 'null'); } catch {}
    app.innerHTML = `<section class="rs2-success" tabindex="-1">${icon.check}<h1 class="rs2-h1">${esc(C.success_title)}</h1>
      ${sent?.model ? `<p><strong>${esc(sent.model)}</strong>${sent.high ? ' · ' + esc(C.offer_label.toLowerCase()) + ' ' + fmt(sent.high) : ''}${sent.target ? ' → ' + esc(sent.target) : ''}</p>` : ''}
      <p>${esc(C.success_sub)}</p>${sent?.email ? `<p class="rs2-small">${esc(sent.email)}</p>` : ''}
      <div class="rs2-ctas"><a class="rs2-btn" href="${esc(location.pathname)}">${esc(C.again)}</a><a class="rs2-btn rs2-btn--ghost" href="${esc(viberLink())}">${icon.viber}${esc(C.viber)}</a></div></section>`;
    form.hidden = true; dockEl.hidden = true;
    app.querySelector('.rs2-success').focus();
    // Fire the lead once per submission (same tab, < 30 min, same page).
    if (sent && !sent.done && Date.now() - sent.t < 18e5 && sent.path === location.pathname) {
      track('sell_lead', {rs2_label: sent.cat, value: sent.high || 0, currency: 'EUR', lead_source: 'sell_page'});
      if (live && marketingOk() && typeof window.fbq === 'function') { try { window.fbq('trackCustom', 'SellLead', {lead_source: 'sell_page', value: sent.high || 0, currency: 'EUR'}); } catch {} }
      try { sessionStorage.setItem('rs2.sent', JSON.stringify({...sent, done: true})); } catch {}
    }
  }

  if (root.querySelector('[data-rs2-posted]')) { success(); return; }
  const srvErr = root.querySelector('[data-rs2-error]');
  // ?upgrade=<product title> from product pages → preselect that product as the trade target.
  try {
    const up = new URLSearchParams(location.search).get('upgrade');
    if (up) { const g = shop.find(x => x.t === up || short(x.t) === up); if (g) st.target = {h: g.h, title: short(g.t), S: priceFor(g, g.vram || null)}; }
  } catch {}
  render();
  track('sell_view', {rs2_label: L}, true);
  if (srvErr) { R('formwrap').hidden = false; fail(srvErr.dataset.message || C.err_unconfirmed); }
}
